import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SEO from "@/components/SEO";
import ShareRow from "@/components/ShareRow";
import { Reveal, MaskLine, SectionHead } from "@/components/Reveal";
import {
  DIMENSIONS, DPR_FEATURES, C3_PROBLEMS, BOOK_PARTS, SUMMITS, TIERS, DOMAINS, EDITIONS,
} from "@/data/content";

const Initiatives = () => (
  <>
    <SEO
      title="Initiatives — C3, Good Governance Book, Delhi Ward Charter & Ward Summits"
      path="/initiatives"
      description="Smart Nagar Nigam's initiatives: Citizen Councillor Connect (C3) with the AI Digital Public Representative, the Good Governance Book across 30 MCD domains, the Delhi Ward Charter, Ward Summit Townhalls and the national replication movement."
    />
    <section className="relative overflow-hidden pt-28 md:pt-52" data-testid="initiatives-hero">
      <div className="mx-auto max-w-[1600px] px-5 pb-20 md:px-10 md:pb-28">
        <MaskLine delay={0.1}>
          <span className="text-xs uppercase tracking-[0.2em] sm:tracking-[0.35em] text-gold md:text-sm">The Initiatives</span>
        </MaskLine>
        <h1 className="mt-6 font-display font-extrabold uppercase leading-[0.95] tracking-tight text-ivory">
          <MaskLine delay={0.25} className="text-[11vw] md:text-[7vw]">One Platform.</MaskLine>
          <MaskLine delay={0.4} className="text-[11vw] md:text-[7vw]">
            <span className="text-outline">Every Ward. Every Citizen.</span>
          </MaskLine>
        </h1>
      </div>
    </section>

    <section className="border-t border-white/10" data-testid="c3-section">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="01" overline="Citizen Councillor Connect (C3)" title="Your Digital Public Representative." />
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal className="space-y-6 text-base leading-relaxed text-white/70 md:text-lg">
            <p>
              C3 is the cornerstone of Smart Nagar Nigam's governance framework — an
              AI-powered constituency management platform: a 5,000-voter connect engine, a
              24/7 AI ward representative, and an issue registration and real-time tracking
              system, built to bridge the gap between every citizen and their elected ward
              leader.
            </p>
            <p>
              At the heart of C3 is the Digital Public Representative — your official
              AI-powered digital office, accessible to every citizen through any smartphone or
              computer, at any time of day or night. Built on a fully customised AI framework,
              it is tailored to the councillor's identity, their area's specific issues, the
              government schemes relevant to their constituency, and their team's workflow. It
              is not a generic chatbot. It is a ward-specific governance interface.
            </p>
            <div>
              <p className="mb-4 text-[11px] uppercase tracking-[0.25em] text-gold">The Problem C3 Solves</p>
              <ul className="space-y-3">
                {C3_PROBLEMS.map((p) => (
                  <li key={p} className="flex gap-3 text-sm leading-relaxed text-white/60">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-gold" aria-hidden="true" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-white/10 bg-coal p-8 md:p-10">
              <p className="text-[11px] uppercase tracking-[0.25em] text-gold">What the DPR Delivers</p>
              <ul className="mt-6 space-y-4">
                {DPR_FEATURES.map((f) => (
                  <li key={f} className="flex gap-3 text-sm leading-relaxed text-white/70">
                    <Check size={16} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <div className="mt-20">
          <Reveal>
            <h3 className="font-display text-2xl font-bold tracking-tight text-ivory md:text-3xl">
              The Five Dimensions of C3 <span className="text-gold">— 20% Each</span>
            </h3>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-white/60 md:text-base">
              Beyond the citizen-facing DPR, C3 assesses each ward councillor across five
              equally weighted governance dimensions — creating a verified, publicly
              accessible performance record for every ward.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-px border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-5">
            {DIMENSIONS.map((d, i) => (
              <Reveal key={d.name} delay={i * 0.05} className="bg-obsidian p-6 md:p-8">
                <p className="font-display text-3xl font-extrabold text-gold">20%</p>
                <h4 className="mt-3 font-display text-base font-bold leading-snug text-ivory">{d.name}</h4>
                <p className="mt-3 text-xs leading-relaxed text-white/55">{d.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="section-light border-t border-white/10" data-testid="book-section">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="02" overline="The Good Governance Book" title="'From Wards to the World' — India's First Ward-Level Compendium." />
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="space-y-6 text-base leading-relaxed text-white/70 md:text-lg lg:col-span-5">
            <p>
              The Good Governance Book — 'From Wards to the World: Good Governance for India's
              Cities' — is simultaneously an achievements document, a crisis diagnostic, a
              solutions marketplace, and a governance vision — all anchored at the ward level.
              No publication in Indian public policy has attempted this combination.
            </p>
            <p>
              It launches at the Conclave and is distributed phygitally — physical copies to
              all 250 ward councillors, every relevant Central ministry, and every Delhi
              Government department; digital versions simultaneously to every journalist,
              think tank researcher, and policy institution across India.
            </p>
            <p className="border-l-2 border-gold pl-6 font-display text-lg font-bold leading-snug text-ivory">
              Its constitutional grounding — Part IX-A, 12th Schedule, 74th Amendment — gives
              it a permanence that transcends the electoral cycle.
            </p>
          </Reveal>
          <div className="border border-white/10 lg:col-span-7">
            {BOOK_PARTS.map((b, i) => (
              <Reveal key={b.part} delay={i * 0.03}>
                <div className="grid gap-2 border-b border-white/10 bg-coal p-6 last:border-b-0 md:grid-cols-12 md:p-8">
                  <p className="font-display text-sm font-bold uppercase tracking-[0.2em] text-gold md:col-span-3">{b.part}</p>
                  <div className="md:col-span-9">
                    <h4 className="font-display text-lg font-bold text-ivory">{b.title}</h4>
                    <p className="mt-2 text-sm leading-relaxed text-white/60">{b.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="border-t border-white/10" data-testid="charter-section">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="03" overline="The Delhi Ward Charter" title="A Public Governance Covenant." />
        <div className="max-w-4xl space-y-6 text-base leading-relaxed text-white/70 md:text-lg">
          <Reveal>
            <p>
              The Delhi Ward Charter is signed live at the Conclave by every participant in
              the room — political leadership, senior bureaucracy, civil society, media, and
              institutional partners. It commits its signatories to measurable accountability
              standards for ward-level governance, drawing its constitutional mandate from
              Article 243-W of the 74th Amendment.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p>
              Every organisation that signs the Charter is permanently inscribed on a
              governance document that will be cited across election cycles, policy circles,
              think tank publications, and media analyses for years.
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="border-t border-white/10 bg-coal" data-testid="summits-section">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="04" overline="Delhi Ward Summit — Townhall Programme" title="The Conclave Is the Spark. The Summits Are the Fire." />
        <p className="mb-12 max-w-3xl text-base leading-relaxed text-white/70 md:text-lg">
          Following the Conclave, four Ward Summit townhall events take the accountability
          conversation directly to citizens — ward by ward, community by community. Each
          councillor engages 300–500 citizens face to face, presents their Ward Vision
          Document, and commits to measurable outcomes. C3 is live-demonstrated and grievance
          registration stations stay open throughout.
        </p>
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-4">
          {SUMMITS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05} className="bg-coal p-8">
              <p className="text-[11px] uppercase tracking-[0.25em] text-gold">{s.n}</p>
              <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-ivory">{s.zone}</h3>
              <p className="mt-3 text-sm font-medium text-white/70">{s.theme}</p>
              <p className="mt-3 border-t border-white/10 pt-3 text-xs leading-relaxed text-white/50">{s.format}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 text-sm leading-relaxed text-white/55">
            At each Summit, Smart Nagar Nigam provides full moderation and production, venue
            and AV, the C3 live demonstration, grievance registration stations, citizen
            outreach across 150+ networks, an on-site media team, Ward Vision Document
            facilitation, and a post-event media package published within 72 hours.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="border-t border-white/10" data-testid="domains-section">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="05" overline="The Complete Opportunity Map" title="30 MCD Domains — Governance Meets Innovation." />
        <p className="mb-12 max-w-3xl text-base leading-relaxed text-white/70 md:text-lg">
          MCD's jurisdiction spans 30 distinct governance domains — each mapped in the Good
          Governance Book and paired with curated startup and innovation lab solutions. A
          framework for industry engagement, government accountability, and citizen
          empowerment alike.
        </p>
        <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {DOMAINS.map((d, i) => (
            <Reveal key={d.n} delay={(i % 3) * 0.04} className="bg-obsidian p-6">
              <div className="flex items-baseline justify-between gap-4">
                <span className="font-display text-sm font-bold tracking-[0.25em] text-gold">{d.n}</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{d.c}</span>
              </div>
              <h3 className="mt-3 font-display text-base font-bold leading-snug text-ivory">{d.d}</h3>
              <p className="mt-2 text-xs leading-relaxed text-white/50">{d.i}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="border-t border-white/10 bg-coal" data-testid="tiers-section">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="06" overline="Industry & Startup Engagement" title="Four Tiers of Partnership." />
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-4">
          {TIERS.map((t, i) => (
            <Reveal key={t.tier} delay={i * 0.05} className="bg-coal p-8">
              <p className="font-display text-sm font-bold uppercase tracking-[0.25em] text-gold">{t.tier}</p>
              <h3 className="mt-3 font-display text-xl font-bold text-ivory">{t.name}</h3>
              <p className="mt-4 text-xs leading-relaxed text-white/55">{t.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="border-t border-white/10" data-testid="movement-section">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="07" overline="The National Movement" title="From Delhi to Every Indian City." />
        <p className="mb-12 max-w-3xl text-base leading-relaxed text-white/70 md:text-lg">
          The Conclave is not a single event — it is the first chapter of a national movement.
          Delhi is Edition One. The model is designed to replicate across India's 4,000+ Urban
          Local Bodies.
        </p>
        <div className="grid gap-px border border-white/10 bg-white/10 lg:grid-cols-3">
          {EDITIONS.map((e, i) => (
            <Reveal key={e.edition} delay={i * 0.06} className="bg-obsidian p-8 md:p-10">
              <p className="text-[11px] uppercase tracking-[0.25em] text-gold">{e.edition}</p>
              <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-ivory">{e.city}</h3>
              <p className="mt-2 text-sm font-medium text-white/70">{e.body} · {e.scale}</p>
              <p className="mt-3 text-xs leading-relaxed text-white/50">{e.context}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-12 max-w-4xl border-l-2 border-gold pl-6 font-display text-lg font-bold leading-snug text-ivory md:text-xl">
            "No initiative in independent India has attempted to simultaneously measure
            ward-level governance across an entire capital city, mobilise elected leadership
            around a public accountability charter, publish that accountability through a
            national media architecture, and then take it back — ward by ward, citizen by
            citizen — to the very communities it is meant to serve. Smart Nagar Nigam is doing
            exactly that."
          </p>
          <div className="mt-12 flex flex-wrap items-center gap-8">
            <Link to="/register" data-testid="initiatives-register-cta" className="inline-flex items-center gap-3 bg-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-obsidian transition-colors duration-300 hover:bg-gold-light">
              Partner With Us <ArrowRight size={16} />
            </Link>
            <ShareRow title="Initiatives — C3, Good Governance Book, Delhi Ward Charter & Ward Summits" path="/initiatives" />
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default Initiatives;
